/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { evaluate, round } from "mathjs";
import { Grid } from "@mui/material";
import FormWrapper from "../common/FormWrapper";
import FormField from "../common/FormField";
import {
  fetchSections, //@info uncomment this before pushing
  pusblishTemplate,
  sectionAutoSave,
  setFormData,
  submitSection,
} from "../../slices/FbSections";
import { SectionSchema } from "../../utils/schemas/initial";
import { notify } from "../../../../bmr/utils/methods/notification";
import DeleteModal from "../../../../bmr/components/common/BaseModal/DeleteModal";
import {
  fetchTemplates,
  updateTemplateStatus,
} from "../../../../bmr/slices/formTemplate";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { fetchDynamicAPIS } from "../../../../utils/utils";
import ReactQuill from "react-quill";

dayjs.extend(customParseFormat);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const FIELD_TYPE_MAP = {
  General: "text",
  Note: "textarea",
  Validations: "text",
  Label: "label",
  Toggle: "switch",
  "Input Radio": "radio-group",
  "Input Dropdown": "select",
  "Input Checkbox": "checkbox-group",
  "Input Text": "text",
  "Input Number": "number",
  "Static Number": "number",
  "Input Email": "email",
  "Input Current Time": "time",
  "Input Date": "date",
  "Input Date and Time": "datetime",
  "Linked Field": "linked-field",
  Hyperlink: "hyperlink",
  "File Upload": "file",
  "Image Upload": "image_upload",
  Barcode: "barcode",
  "Auto Increment": "text",
};

const LAYOUT_OPTIONS = {
  12: { gridSize: 12 },
  6: { gridSize: 6 },
  4: { gridSize: 4 },
  3: { gridSize: 3 },
  2: { gridSize: 2 },
};

// Formats the API accepts in date_display_format
const VALID_DATE_FORMATS = [
  "DD/MM/YY",
  "MM/DD/YY",
  "YY/MM/DD",
  "DD-MM-YY",
  "MM-DD-YY",
  "YY-MM-DD",
  "DD/MM/YYYY",
  "YYYY-MM-DD",
  "MM",
  "YYYY",
  "MM/YYYY",
  "MM-YYYY",
  "MM/YY",
  "MM-YY",
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Sort fields by order and group consecutive same-layout fields together. */
const buildFieldGroups = (fields) => {
  const sorted = [...fields].sort((a, b) => a.order - b.order);
  const groups = [];
  let currentLayout = null;
  let currentGroup = [];

  sorted.forEach((field) => {
    const layout = field.property.field_layout || 12;
    if (layout !== currentLayout) {
      if (currentGroup.length)
        groups.push({ layout: currentLayout, fields: currentGroup });
      currentGroup = [];
      currentLayout = layout;
    }
    currentGroup.push({
      ...field,
      type: FIELD_TYPE_MAP[field.property.field_type] || "text",
      required: field.property.general?.required || false,
    });
  });

  if (currentGroup.length)
    groups.push({ layout: currentLayout, fields: currentGroup });
  return groups;
};

const resolveDateConstraints = (inputDate = {}) => {
  const {
    past_only = false,
    future_only = false,
    min_date_enabled = false,
    max_date_enabled = false,
    min_date = "",
    max_date = "",
  } = inputDate;

  let minDate;
  if (min_date_enabled && min_date) {
    // Explicit min date provided — parse using any valid format strictly
    const parsed = dayjs(min_date, VALID_DATE_FORMATS, true);
    minDate = parsed.isValid() ? parsed : undefined;
  } else if (future_only) {
    // No explicit date — block everything before the start of today
    minDate = dayjs().startOf("day");
  }

  let maxDate;
  if (max_date_enabled && max_date) {
    // Explicit max date provided — parse using any valid format strictly
    const parsed = dayjs(max_date, VALID_DATE_FORMATS, true);
    maxDate = parsed.isValid() ? parsed : undefined;
  } else if (past_only) {
    // No explicit date — block everything after the end of today
    maxDate = dayjs().endOf("day");
  }

  return { minDate, maxDate };
};

const preferServerValue = (serverValue, localValue) =>
  serverValue !== undefined ? serverValue : localValue;

const mergeFieldPropertyForPreview = (
  serverProperty = {},
  localProperty = {},
) => ({
  ...serverProperty,
  ...localProperty,
  options: preferServerValue(serverProperty?.options, localProperty?.options),
  conditions: preferServerValue(
    serverProperty?.conditions,
    localProperty?.conditions,
  ),
  option_conditions: preferServerValue(
    serverProperty?.option_conditions,
    localProperty?.option_conditions,
  ),
  input_dropdown: {
    ...serverProperty?.input_dropdown,
    ...localProperty?.input_dropdown,
  },

  input_radio: {
    ...serverProperty?.input_radio,
    ...localProperty?.input_radio,
  },

  input_checkbox: {
    ...serverProperty?.input_checkbox,
    ...localProperty?.input_checkbox,
  },
  validations: preferServerValue(
    serverProperty?.validations,
    localProperty?.validations,
  ),
  linked_field: preferServerValue(
    serverProperty?.linked_field,
    localProperty?.linked_field,
  ),
  input_calculation_variables: preferServerValue(
    serverProperty?.input_calculation_variables,
    localProperty?.input_calculation_variables,
  ),
  input_current_time: {
    ...serverProperty?.input_current_time,
    ...localProperty?.input_current_time,
    dependency: preferServerValue(
      serverProperty?.input_current_time?.dependency,
      localProperty?.input_current_time?.dependency,
    ),
    time_greater_than: preferServerValue(
      serverProperty?.input_current_time?.time_greater_than,
      localProperty?.input_current_time?.time_greater_than,
    ),
    time_less_than: preferServerValue(
      serverProperty?.input_current_time?.time_less_than,
      localProperty?.input_current_time?.time_less_than,
    ),
  },
  input_date: {
    ...serverProperty?.input_date,
    ...localProperty?.input_date,
    min_date_enabled: preferServerValue(
      serverProperty?.input_date?.min_date_enabled,
      localProperty?.input_date?.min_date_enabled,
    ),
    max_date_enabled: preferServerValue(
      serverProperty?.input_date?.max_date_enabled,
      localProperty?.input_date?.max_date_enabled,
    ),
    min_date: preferServerValue(
      serverProperty?.input_date?.min_date,
      localProperty?.input_date?.min_date,
    ),
    max_date: preferServerValue(
      serverProperty?.input_date?.max_date,
      localProperty?.input_date?.max_date,
    ),
    past_only: preferServerValue(
      serverProperty?.input_date?.past_only,
      localProperty?.input_date?.past_only,
    ),
    future_only: preferServerValue(
      serverProperty?.input_date?.future_only,
      localProperty?.input_date?.future_only,
    ),
  },
});

const mergeSectionsForPreview = (serverSections = [], localSections = []) => {
  const localSectionsMap = new Map(
    (localSections || []).map((section) => [section?.section_uuid, section]),
  );

  const mergedSections = (serverSections || []).map(
    (serverSection, sectionIndex) => {
      const localSection = localSectionsMap.get(serverSection?.section_uuid);
      if (!localSection) {
        return serverSection;
      }

      const serverFields = serverSection?.fields || [];
      const localFieldsMap = new Map(
        (localSection?.fields || []).map((field) => [field?.input_uuid, field]),
      );

      const mergedFields = serverFields.map((serverField, fieldIndex) => {
        const localField = localFieldsMap.get(serverField?.input_uuid);
        if (!localField) {
          return serverField;
        }

        return {
          ...serverField,
          label: localField?.label ?? serverField?.label,
          placeholder: localField?.placeholder ?? serverField?.placeholder,
          order: localField?.order ?? serverField?.order ?? fieldIndex + 1,
          value: localField?.value ?? serverField?.value,
          property: mergeFieldPropertyForPreview(
            serverField?.property,
            localField?.property,
          ),
        };
      });

      const serverFieldIds = new Set(
        serverFields.map((field) => field?.input_uuid),
      );
      const localOnlyFields = (localSection?.fields || []).filter(
        (field) => !serverFieldIds.has(field?.input_uuid),
      );

      return {
        ...serverSection,
        name: localSection?.name ?? serverSection?.name,
        description: localSection?.description ?? serverSection?.description,
        order: localSection?.order ?? serverSection?.order ?? sectionIndex + 1,
        property: {
          ...serverSection?.property,
          ...localSection?.property,
        },
        fields: [...mergedFields, ...localOnlyFields],
      };
    },
  );

  const serverSectionIds = new Set(
    (serverSections || []).map((section) => section?.section_uuid),
  );
  const localOnlySections = (localSections || []).filter(
    (section) => !serverSectionIds.has(section?.section_uuid),
  );

  return [...mergedSections, ...localOnlySections];
};
const modules = {
  toolbar: {
    container: "#quill-toolbar",
  },
  clipboard: { matchVisual: false },
};

const formats = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "align",
  "list",
  "bullet",
  "link",
  "image",
];
// ---------------------------------------------------------------------------
// DynamicFormGenerator
// ---------------------------------------------------------------------------
const getDependencyValues = (dependencies = [], allFields = []) => {
  return dependencies.map((dep) => {
    const matchedField = allFields.find((f) => f.input_uuid === dep.field_uuid);

    return {
      query_param: dep.query_param,
      value: matchedField?.value,
    };
  });
};

const getFieldDependencies = (field, allFields = []) => {
  const cfg =
    field?.property?.input_dropdown ||
    field?.property?.input_radio ||
    field?.property?.input_checkbox;

  // PRIORITY 1 -> master_dependencies
  if (cfg?.master_dependencies?.length) {
    return getDependencyValues(cfg.master_dependencies, allFields);
  }

  // PRIORITY 2 -> depends_on
  if (cfg?.depends_on) {
    const matchedField = allFields.find((f) => f.input_uuid === cfg.depends_on);

    return [
      {
        query_param: null,
        value: matchedField?.value,
      },
    ];
  }

  return [];
};

const DynamicFormGenerator = ({
  formJson,
  onSubmit,
  loading,
  formData,
  setDFormData,
  dynamicOptions = {},
  fieldLoading = {},
}) => {
  const liveOptionsState = useSelector(
    (state) => state.bmrFbProperty?.options || {},
  );
  const quillRef = useRef({});
  const [values, setValues] = useState({});

  const formSections = useMemo(
    () =>
      formJson.map((section) => ({
        ...section,
        fieldGroups: buildFieldGroups(section.fields),
      })),
    [formJson],
  );

  /** Options keyed by input_uuid — for radio / select / checkbox fields. */
  const applyOptionConditions = (formJson, formData, baseOptions) => {
    const updatedOptions = { ...baseOptions };

    if (!formData?.sections?.length) return updatedOptions;

    const allFields = formData.sections.flatMap((s) => s.fields || []);

    allFields.forEach((field) => {
      const selectedValue = field.value;

      const allOpts = field?.property?.options?.options || [];

      const selectedOption = allOpts.find(
        (opt) => (opt.value ?? opt.option_uuid) === selectedValue,
      );

      if (!selectedOption) return;

      const selectedOptionUuid = selectedOption.option_uuid;

      const optionConditions = field?.property?.option_conditions || [];

      optionConditions.forEach((cond) => {
        if (cond.fbt_option_uuid !== selectedOptionUuid) return;

        cond.targets?.forEach((target) => {
          const targetFieldUuid = target.target_field_uuid;
          const allowedOptions = target?.filter_values?.allowed_options || [];

          if (updatedOptions[targetFieldUuid]) {
            updatedOptions[targetFieldUuid] = updatedOptions[
              targetFieldUuid
            ].filter((opt) => allowedOptions.includes(opt.option_uuid));
          }
        });
      });
    });

    return updatedOptions;
  };

  const handleQuillChange = (val, id) => {
    setValues((prev) => ({ ...prev, [id]: val }));
    handleFieldChange(id, val);
  };

  const optionsByField = useMemo(() => {
    const map = {};
    formJson.forEach((section) =>
      section.fields.forEach((field) => {
        const liveOptions = (
          dynamicOptions?.[field.input_uuid] ||
          liveOptionsState?.[field.input_uuid]
        )?.options;
        const schemaOptions = field?.property?.options?.options || [];
        const opts =
          Array.isArray(liveOptions) && liveOptions.length
            ? liveOptions
            : schemaOptions;
        map[field.input_uuid] = opts.map((opt) => ({
          value: opt.value !== undefined ? opt.value : opt.option_uuid,
          label: opt.label,
          option_uuid: opt.option_uuid || opt.value,
        }));
      }),
    );
    // return map;
    return applyOptionConditions(formJson, formData, map);
  }, [formJson, liveOptionsState, dynamicOptions]);

  /** Flat option-uuid → label dictionary for Linked Fields. */
  const allOptionsDict = useMemo(() => {
    const dict = {};
    formJson.forEach((section) =>
      section.fields.forEach((field) => {
        const liveOptions = liveOptionsState?.[field.input_uuid]?.options;
        const schemaOptions = field?.property?.options?.options || [];
        const opts =
          Array.isArray(liveOptions) && liveOptions.length
            ? liveOptions
            : schemaOptions;
        opts.forEach((opt) => {
          dict[opt.option_uuid] = opt.label;
        });
      }),
    );
    return dict;
  }, [formJson, liveOptionsState]);

  // ── Conditional visibility ─────────────────────────────────────────────────
  const hiddenFields = useMemo(() => {
    const hidden = [];
    if (!formData?.sections?.length) return hidden;

    const allFields = formData.sections.flatMap((s) => s.fields || []);
    allFields
      .filter((f) => f.property?.conditions?.length)
      .forEach((fieldItem) => {
        const selectedField = allFields.find(
          (f) => f.input_uuid === fieldItem.input_uuid,
        );
        const selectedValue = selectedField?.value;

        // Get all possible options for this field to map value back to option_uuid
        const liveOptions =
          liveOptionsState?.[fieldItem.input_uuid]?.options || [];
        const schemaOptions = fieldItem?.property?.options?.options || [];
        const allOpts = liveOptions.length ? liveOptions : schemaOptions;

        // Map selected value(s) to their corresponding option_uuid(s)
        const selectedValues = Array.isArray(selectedValue)
          ? selectedValue
          : [selectedValue];
        const selectedOptionUuids = allOpts
          .filter((opt) =>
            selectedValues.includes(
              opt.value !== undefined ? opt.value : opt.option_uuid,
            ),
          )
          .map((opt) => opt.option_uuid);

        const conditionsByTarget = {};
        fieldItem.property.conditions.forEach((cond) => {
          const targets = Array.isArray(cond.target_input_field_uuid)
            ? cond.target_input_field_uuid
            : cond.target_input_field_uuid
            ? [cond.target_input_field_uuid]
            : [];
          targets.forEach((uuid) => {
            if (!conditionsByTarget[uuid]) conditionsByTarget[uuid] = [];
            conditionsByTarget[uuid].push(cond.fbt_option_uuid);
          });
        });

        Object.entries(conditionsByTarget).forEach(
          ([targetUuid, optionUuids]) => {
            const anyMatch = selectedOptionUuids.some((uuid) =>
              optionUuids.includes(uuid),
            );
            if (!anyMatch) hidden.push(targetUuid);
          },
        );
      });

    return hidden;
  }, [formData, liveOptionsState]);

  // ── Field value updater ────────────────────────────────────────────────────
  const handleFieldChange = (fieldUuid, value) => {
    setDFormData((prev) => {
      const allFields = prev.sections.flatMap((s) => s.fields || []);

      // Collect UUIDs of dependent fields that depend on the changed field,
      // so their selected value can be cleared when the parent changes.
      const dependentFieldUuids = new Set();
      allFields.forEach((f) => {
        const cfg =
          f?.property?.input_dropdown ||
          f?.property?.input_radio ||
          f?.property?.input_checkbox;
        if (!cfg?.is_dependent) return;

        const deps = cfg?.master_dependencies?.length
          ? cfg.master_dependencies.map((d) => d.field_uuid)
          : cfg?.depends_on
          ? [cfg.depends_on]
          : [];

        if (deps.includes(fieldUuid)) {
          dependentFieldUuids.add(f.input_uuid);
        }
      });

      return {
        ...prev,
        sections: prev.sections.map((section) => ({
          ...section,
          fields: section.fields.map((field) => {
            if (field.input_uuid === fieldUuid) return { ...field, value };
            // Clear value of any dependent field whose parent just changed
            if (dependentFieldUuids.has(field.input_uuid)) {
              return { ...field, value: "" };
            }
            return field;
          }),
        })),
      };
    });
  };

  const normalizeDateFormat = (format = "") => {
    if (!format) return "DD/MM/YY";
    return format.replace(/:/g, "/").replace(/-/g, "/").replace(/\s+/g, "");
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <FormWrapper onSubmit={onSubmit} loading={loading} className="p-3">
      {/* ── Single outer border containing all sections ── */}
      <Box
        sx={{
          // border: "1px solid #E0E0E0",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {formSections.map((section, index) => (
          <Box key={section?.section_uuid}>
            {/* ── Section header ── */}
            {(section?.property?.display_details ||
              section?.property?.display_field_number) && (
              <Box
                sx={{
                  minHeight: "56px",
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                }}
              >
                <Box>
                  {section?.property?.display_details &&
                  section?.property?.display_field_number ? (
                    <>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {`${index + 1}. ${section?.name}`}
                      </Typography>
                      {section?.description && (
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 400, color: "text.secondary" }}
                        >
                          {section?.description}
                        </Typography>
                      )}
                    </>
                  ) : section?.property?.display_field_number ? (
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {`${index + 1}.`}
                    </Typography>
                  ) : section?.property?.display_details ? (
                    <>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, textTransform: "uppercase" }}
                      >
                        {section?.name}
                      </Typography>
                      {section?.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 400,
                            fontSize: "0.875rem",
                            color: "text.secondary",
                            textTransform: "capitalize",
                          }}
                        >
                          {section?.description}
                        </Typography>
                      )}
                    </>
                  ) : null}
                </Box>
              </Box>
            )}

            {/* ── Section fields ── */}
            <Box
              className="p-3 px-2"
              sx={{
                ...((section?.property?.type === "card" ||
                  section?.property?.type === "group") && {
                  border: "1px solid #E0E0E0",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
                  m: 1,
                  p: 1,
                }),
              }}
            >
              {section.fieldGroups.map((group, groupIndex) => (
                <Grid key={groupIndex} container spacing={2}>
                  {group.fields.map((field) => {
                    // if (hiddenFields.includes(field.input_uuid)) return null;
                    const cfg =
                      field?.property?.input_dropdown ||
                      field?.property?.input_radio ||
                      field?.property?.input_checkbox;

                    const allFields = formData.sections.flatMap(
                      (s) => s.fields || [],
                    );

                    const dependencyValues = getFieldDependencies(
                      field,
                      allFields,
                    );

                    const hasDependencyValue =
                      dependencyValues.length > 0 &&
                      dependencyValues.every((dep) => dep.value);

                    // ── FIELD VISIBILITY LOGIC ─────────────────────────────

                    const isHidden =
                      cfg?.is_dependent &&
                      cfg?.is_visible === true &&
                      !hasDependencyValue;

                    // If hidden, return null immediately to prevent rendering or API side-effects
                    if (isHidden) return null;

                    // existing conditional visibility
                    if (hiddenFields.includes(field.input_uuid)) return null;
                    const datetimeFormat = normalizeDateFormat(
                      field?.property?.input_date_and_time?.date_display_format,
                    );
                    const timedateFormat =
                      field?.property?.input_date_and_time?.time_format ===
                      "24_hour"
                        ? "HH:mm"
                        : "hh:mm A";

                    const dateTimeFormat = `${datetimeFormat} ${timedateFormat}`;

                    const rawMin =
                      field?.property?.input_date_and_time?.min_datetime;
                    const rawMax =
                      field?.property?.input_date_and_time?.max_datetime;

                    const parsedMin = rawMin
                      ? dayjs(rawMin, dateTimeFormat, true)
                      : null;

                    const parsedMax = rawMax
                      ? dayjs(rawMax, dateTimeFormat, true)
                      : null;
                    // Live value from formData, falling back to static field.value
                    const fieldValue =
                      formData.sections
                        .flatMap((s) => s.fields)
                        .find((f) => f.input_uuid === field.input_uuid)
                        ?.value ??
                      field.value ??
                      "";

                    const showFormula = field?.property?.calculation
                      ?.display_formula
                      ? JSON.parse(field.property.calculation.display_formula)
                      : false;
                    const calcProp = field?.property?.calculation;

                    // ── Derive date props from API shape ───────────────────
                    const inputDate = field?.property?.input_date || {};

                    // date_display_format: validated against known formats
                    const dateFormat = VALID_DATE_FORMATS.includes(
                      inputDate.date_display_format,
                    )
                      ? inputDate.date_display_format
                      : "DD/MM/YY";

                    // date_range: true → range picker
                    const isRange = inputDate.date_range === true;

                    // minDate / maxDate as dayjs objects (or undefined)
                    const { minDate, maxDate } =
                      resolveDateConstraints(inputDate);

                    // ── onChange handler ───────────────────────────────────
                    const handleChange = (input) => {
                      let finalValue;

                      switch (field.property.field_type) {
                        case "Input Date":
                          if (isRange) {
                            finalValue =
                              input && typeof input === "object"
                                ? {
                                    start: input.start
                                      ? dayjs(
                                          input.start,
                                          dateFormat,
                                          true,
                                        ).format("YYYY-MM-DD")
                                      : null,
                                    end: input.end
                                      ? dayjs(
                                          input.end,
                                          dateFormat,
                                          true,
                                        ).format("YYYY-MM-DD")
                                      : null,
                                  }
                                : { start: null, end: null };
                          } else {
                            finalValue =
                              input && typeof input === "string"
                                ? dayjs(input, dateFormat, true).format(
                                    "YYYY-MM-DD",
                                  )
                                : "";
                          }
                          break;

                        case "Input Current Time":
                          finalValue = input || "";
                          break;

                        case "Input Date and Time":
                          finalValue = input ? dayjs(input).toISOString() : "";
                          break;

                        default:
                          if (
                            typeof input === "object" &&
                            input !== null &&
                            input.$d
                          ) {
                            finalValue = input;
                          } else if (input?.target) {
                            finalValue = input.target.value;
                          } else {
                            finalValue = input;
                          }
                          break;
                      }

                      handleFieldChange(field.input_uuid, finalValue);
                    };

                    return (
                      <Grid
                        key={field.input_uuid}
                        item
                        xs={12}
                        sm={LAYOUT_OPTIONS[group.layout]?.gridSize || 12}
                      >
                        <Box sx={{ paddingTop: "1rem", padding: 0 }}>
                          {/* ── Field label heading ── */}
                          {field.type === "label" ? (
                            <Typography
                              variant="body"
                              className="text-sm font-bold"
                            >
                              {field?.label}
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                minHeight: "1.5rem",
                                pb: "0.625rem",
                                visibility: field?.property?.general
                                  ?.remove_heading
                                  ? "hidden"
                                  : "visible",
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                className="inline-block font-normal"
                                sx={{ paddingBottom: "0.625rem" }}
                              >
                                <Box
                                  component="span"
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {field?.label}{" "}
                                </Box>
                                {field?.property?.general?.required && (
                                  <Box
                                    component="span"
                                    className="text-red-500"
                                    sx={{ ml: 0.5 }}
                                  >
                                    *
                                  </Box>
                                )}
                              </Typography>
                            </Box>
                          )}

                          {/* ── Field input ── */}
                          {(() => {
                            //@info commented these handling error validation from the utils for current time

                            if (
                              field.property.field_type === "Input Current Time"
                            ) {
                              const timeProps =
                                field.property.input_current_time || {};
                            }

                            if (field.property.field_type === "Calculation") {
                              return (
                                <>
                                  <FormField
                                    name={field.input_uuid}
                                    type="number"
                                    label=""
                                    fullWidth
                                    value={fieldValue}
                                    disabled
                                    // size="small"
                                    endAdornment={
                                      <InputAdornment
                                        position="end"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mr: 1.5,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "#6b7280",
                                            fontSize: "0.8rem",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {calcProp?.unit_of_measurement || ""}
                                        </Typography>
                                      </InputAdornment>
                                    }
                                  />
                                  {showFormula && calcProp?.formula && (
                                    <Typography variant="caption">
                                      Formula: {field?.parsedFormula}
                                    </Typography>
                                  )}
                                </>
                              );
                            } else if (
                              field.property.field_type === "Linked Field"
                            ) {
                              return (
                                <FormField
                                  name={field.input_uuid}
                                  label=""
                                  fullWidth
                                  value={
                                    allOptionsDict[fieldValue] ||
                                    fieldValue ||
                                    field.property.linked_field?.linked_field ||
                                    ""
                                  }
                                  disabled
                                  size="small"
                                />
                              );
                            } else if (
                              field.property.field_type === "Rich Text Editor"
                            ) {
                              return (
                                <>
                                  <div className="border-2 rounded-2xl disclaimer-quill-wrapper">
                                    <ReactQuill
                                      key={field.input_uuid}
                                      value={values[field.input_uuid] || ""}
                                      onChange={(val) =>
                                        handleQuillChange(val, field.input_uuid)
                                      }
                                      theme="snow"
                                      modules={{
                                        toolbar: [
                                          [{ header: [1, 2, false] }],
                                          [
                                            "bold",
                                            "italic",
                                            "underline",
                                            "strike",
                                          ],
                                          [{ color: [] }],
                                          [{ align: [] }],
                                          [
                                            { list: "ordered" },
                                            { list: "bullet" },
                                          ],
                                          ["link", "image"],
                                        ],
                                      }}
                                      formats={formats}
                                    />
                                  </div>
                                </>
                              );
                            } else {
                              const isLoading = fieldLoading[field.input_uuid];

                              if (
                                [
                                  "radio-group",
                                  "select",
                                  "checkbox-group",
                                ].includes(field.type) &&
                                isLoading
                              ) {
                                return (
                                  <Skeleton
                                    variant="rectangular"
                                    height={40}
                                    sx={{ borderRadius: "6px" }}
                                  />
                                );
                              }

                              return (
                                <FormField
                                  // rules={customRules}
                                  // name={`sections.${index}.fields.0.${field?.label}`}
                                  name={field.input_uuid}
                                  type={field.type}
                                  value={fieldValue}
                                  options={(() => {
                                    if (
                                      ![
                                        "radio-group",
                                        "select",
                                        "checkbox-group",
                                      ].includes(field.type)
                                    )
                                      return [];

                                    const cfg =
                                      field?.property?.input_dropdown ||
                                      field?.property?.input_radio ||
                                      field?.property?.input_checkbox;

                                    const allFields = formData.sections.flatMap(
                                      (s) => s.fields || [],
                                    );

                                    const dependencyValues =
                                      getFieldDependencies(field, allFields);

                                    const hasDependencyValue =
                                      dependencyValues.every(
                                        (dep) => dep.value,
                                      );

                                    // DATA VISIBILITY LOGIC

                                    // CASE 1:
                                    // preload/show data immediately
                                    if (cfg?.is_data_visible === true) {
                                      return (
                                        optionsByField[field.input_uuid] || []
                                      );
                                    }

                                    // CASE 2:
                                    // field visible only after dependency selected
                                    if (cfg?.is_visible === true) {
                                      return hasDependencyValue
                                        ? optionsByField[field.input_uuid] || []
                                        : [];
                                    }
                                    // CASE 3:
                                    // preload data immediately
                                    return (
                                      optionsByField[field.input_uuid] || []
                                    );
                                  })()}
                                  row={
                                    field.type === "radio-group"
                                      ? !!field?.property?.input_radio
                                          ?.is_horizontal
                                      : field.type === "checkbox-group"
                                      ? !!field?.property?.input_checkbox
                                          ?.alignment_horizontal
                                      : false
                                  }
                                  nomargin
                                  placeholder={
                                    field.placeholder ||
                                    `Enter ${
                                      field.label?.toLowerCase() || "value"
                                    }`
                                  }
                                  tooltip={field.placeholder}
                                  // hasbothlabels={field.type === "Toggle"}
                                  label1={field?.property?.toggle?.toggle_msg_1}
                                  label2={field?.property?.toggle?.toggle_msg_2}
                                  disabled={
                                    field?.property?.general?.disable
                                  }
                                  endAdornment={(() => {
                                    const unit =
                                      field.property.field_type ===
                                      "Calculation"
                                        ? field?.property?.calculation
                                            ?.unit_of_measurement
                                        : field.type === "number"
                                        ? field?.property?.input_number
                                            ?.measurement_unit
                                        : field.type === "text"
                                        ? field?.property?.input_text
                                            ?.measurement_unit_text
                                        : "";

                                    if (!unit) return null;

                                    return (
                                      <InputAdornment
                                        position="end"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mr: 1.5,
                                        }}
                                      >
                                        <Typography
                                          variant="body2"
                                          sx={{
                                            color: "#6b7280",
                                            fontSize: "0.8rem",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {unit}
                                        </Typography>
                                      </InputAdornment>
                                    );
                                  })()}
                                  onChange={handleChange}
                                  // ── Date props ────────────────────────────────────
                                  dateFormat={dateFormat}
                                  dateRange={isRange}
                                  minDate={minDate}
                                  maxDate={maxDate}
                                  // ── DateTime props ────────────────────────────────
                                  combineDate={
                                    field.property.field_type ===
                                    "Input Date and Time"
                                      ? field?.property?.input_date_and_time
                                          ?.date_display_format
                                      : "DD/MM/YY"
                                  }
                                  combineTime={
                                    field.property.field_type ===
                                    "Input Date and Time"
                                      ? field?.property?.input_date_and_time
                                          ?.time_format
                                      : "HH:mm"
                                  }
                                  // ── Time props ────────────────────────────────────
                                  timeFormat={
                                    field.property.field_type ===
                                    "Input Current Time"
                                      ? field?.property?.input_current_time
                                          ?.time_format
                                      : "hh:mm A"
                                  }
                                  currentTime={
                                    field.property.field_type ===
                                    "Input Current Time"
                                      ? field?.property?.input_current_time
                                          ?.current_time
                                      : false
                                  }
                                  defaultTime={
                                    field.property.field_type ===
                                    "Input Current Time"
                                      ? field?.property?.input_current_time
                                          ?.default_time
                                      : ""
                                  }
                                  // ── Other ─────────────────────────────────────────
                                  linkLabel={field?.property?.hyperlink?.name}
                                  linkUrl={field?.property?.hyperlink?.url}
                                  defaultValue={
                                    field.property.field_type === "Input Text"
                                      ? field?.property?.input_text?.default_val
                                      : field.property.field_type ===
                                        "Input Number"
                                      ? field?.property?.input_number
                                          ?.default_val ?? null
                                      : field.property.field_type ===
                                        "Auto Increment"
                                      ? field?.property?.auto_increment
                                          ?.default_val
                                      : ""
                                  }
                                  minDateTime={
                                    field?.property?.input_date_and_time
                                      ?.min_datetime_enabled &&
                                    parsedMin?.isValid()
                                      ? parsedMin
                                      : undefined
                                  }
                                  maxDateTime={
                                    field?.property?.input_date_and_time
                                      ?.max_datetime_enabled &&
                                    parsedMax?.isValid()
                                      ? parsedMax
                                      : undefined
                                  }
                                  currentDate={
                                    field?.property?.input_date?.current_date
                                  }
                                  defaultDate={
                                    field?.property?.input_date?.default_date
                                  }
                                  dateReadonly={
                                    field?.property?.input_date?.current_date ||
                                    field?.property?.input_date?.default_date
                                  }
                                  currentDateTime={
                                    field?.property?.input_date_and_time
                                      ?.current_datetime
                                  }
                                  defaultDateTime={
                                    field?.property?.input_date_and_time
                                      ?.default_datetime
                                  }
                                  futureOnly={
                                    field?.property?.input_date_and_time
                                      ?.future_only_datetime
                                  }
                                  pastOnly={
                                    field?.property?.input_date_and_time
                                      ?.past_only_datetime
                                  }
                                />
                              );
                            }
                          })()}
                        </Box>

                        {field?.property?.note?.message_note && (
                          <Typography
                            variant="body"
                            sx={{ mb: 1, color: "#FB9D2C" }}
                          >
                            {`Note : ${field.property.note.message_note}`}
                          </Typography>
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
              ))}
              {section?.property?.type === "group" && (
                <Box display="flex" justifyContent="end" className="pt-2">
                  <Button
                    variant="contained"
                    disabled={section?.property?.type === "group"}
                  >
                    Add
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </FormWrapper>
  );
};

// ---------------------------------------------------------------------------
// DynamicPreview
// ---------------------------------------------------------------------------

const DynamicPreview = ({ handleCloseDrawer, cardId, getValues }) => {
  const { id } = useParams();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const apiCacheRef = useRef({});
  const fetchingRef = useRef({});
  const [fieldLoading, setFieldLoading] = useState({});
  const FORM_SCHEMA_UPDATED = useSelector(
    (state) => state.bmrFbSections.formData.sections,
  );
  const { fbTemplate } = useSelector((state) => state.bmrFbl);
  const templateName = fbTemplate.items.find(
    (name) => name?.id == (id ?? cardId),
  );

  const dispatch = useDispatch();
  const [formData, setDynamicFormData] = useState({
    sections: FORM_SCHEMA_UPDATED || [],
  });
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const handleCloseConfirmation = () => setOpenConfirmation(false);
  const handleSubmit = () => dispatch(submitSection(id ?? cardId));

  const handlePublish = async () => {
    try {
      const response = await dispatch(pusblishTemplate(id ?? cardId)).unwrap();
      if (!location.pathname.includes("preview")) handleCloseDrawer();
      dispatch(updateTemplateStatus({ id, status: response.data.status }));
      dispatch(
        fetchTemplates({
          page: 1,
          per_page: 12,
          search: null,
          status: "",
          version: "",
        }),
      );
    } catch (error) {
      notify.error(error?.message || error?.data?.message);
    }
  };

  useEffect(() => {
    if (FORM_SCHEMA_UPDATED)
      setDynamicFormData({ sections: FORM_SCHEMA_UPDATED });
  }, [FORM_SCHEMA_UPDATED]);

  const injectDefaultValues = useCallback(
    (sections) =>
      sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          const hasValue =
            field.value !== undefined &&
            field.value !== null &&
            field.value !== "";
          return {
            ...field,
            value: hasValue
              ? field.value
              : field?.property?.linked_field?.default_val ??
                field?.property?.input_text?.default_val ??
                field?.property?.auto_increment?.default_val ??
                field?.property?.input_number?.default_val ??
                "",
          };
        }),
      })),
    [],
  );

  const fetchSetFormData = useCallback(async () => {
    try {
      setLoading(true);
      const currentFormData =
        typeof getValues === "function" ? getValues("sections") : null;

      if (
        Array.isArray(currentFormData) &&
        currentFormData.length &&
        !cardId &&
        templateName?.status !== 2
      ) {
        await dispatch(
          sectionAutoSave({
            id,
            data: { sections: currentFormData },
          }),
        ).unwrap();
      }

      const response = await dispatch(fetchSections(id ?? cardId)).unwrap();
      const serverSections =
        response.data.sections.length > 0
          ? response.data.sections
          : [SectionSchema];
      const mergedSections =
        Array.isArray(currentFormData) && currentFormData.length
          ? mergeSectionsForPreview(serverSections, currentFormData)
          : serverSections;
      const modifiedSections = injectDefaultValues(mergedSections);
      dispatch(setFormData({ sections: modifiedSections }));
      setDynamicFormData({ sections: modifiedSections });
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  }, [cardId, dispatch, getValues, id, injectDefaultValues]);

  useEffect(() => {
    fetchSetFormData();
  }, [fetchSetFormData]);

  const getParsedFormula = (formVariables, formula) => {
    let parsed = formula;
    Object.entries(formVariables).forEach(([key, value]) => {
      parsed = parsed.replace(new RegExp(`\\b${key}\\b`, "g"), value);
    });
    return parsed;
  };

  useEffect(() => {
    if (!formData?.sections?.length) return;

    const inputValues = {};
    const fieldInputLabels = {};
    const updatedFields = [];

    for (const section of formData.sections) {
      for (const field of section.fields || []) {
        const { input_uuid, value, property } = field;
        fieldInputLabels[input_uuid] = field.label;
        inputValues[input_uuid] =
          property?.field_type === "Input Number" && value
            ? parseFloat(value) ?? 0
            : value;
      }
    }

    for (const section of formData.sections) {
      for (const field of section.fields || []) {
        const { input_uuid, value: currentValue, property } = field;
        const {
          field_type,
          calculation,
          input_calculation_variables,
          linked_field,
        } = property || {};

        if (field_type === "Calculation" && calculation?.formula) {
          const variables = {};
          const formVariables = {};
          for (const variable of input_calculation_variables || []) {
            formVariables[variable.name] =
              fieldInputLabels[variable.linked_field_uuid];
            variables[variable.name] =
              inputValues[variable.linked_field_uuid] ||
              (!isNaN(variable.default_val) &&
                parseFloat(variable.default_val)) ||
              0;
          }
          try {
            let result = round(
              evaluate(calculation.formula, variables),
              calculation.decimal_places || 4,
            );
            if (calculation.decimal_places != null) {
              result = parseFloat(
                Number(result).toFixed(calculation.decimal_places),
              );
            }
            const resultStr = result?.toString() ?? "";
            if (currentValue !== resultStr) {
              updatedFields.push({
                fieldUuid: input_uuid,
                value: resultStr,
                parsedFormula: getParsedFormula(
                  formVariables,
                  calculation.formula,
                ),
              });
            }
          } catch (err) {
            console.warn(`Invalid formula for "${input_uuid}": ${err.message}`);
          }
        }

        if (field_type === "Linked Field" && linked_field?.linked_field) {
          const newValue = inputValues[linked_field.linked_field];
          updatedFields.push({
            fieldUuid: input_uuid,
            value:
              newValue !== undefined && newValue !== null && newValue !== ""
                ? newValue
                : linked_field.default_val || "",
          });
        }
      }
    }

    if (!updatedFields.length) return;

    setDynamicFormData((prev) => {
      const updatesMap = Object.fromEntries(
        updatedFields.map(({ fieldUuid, value, parsedFormula }) => [
          fieldUuid,
          { value, parsedFormula },
        ]),
      );
      const newSections = prev.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) => {
          const update = updatesMap[field.input_uuid];
          if (update && field.value !== update.value) {
            return {
              ...field,
              value: update.value,
              parsedFormula: update.parsedFormula,
            };
          }
          return field;
        }),
      }));
      return JSON.stringify(newSections) === JSON.stringify(prev.sections)
        ? prev
        : { ...prev, sections: newSections };
    });
  }, [formData]);

  const [fetchedFields, setFetchedFields] = useState({});
  const [dynamicOptions, setDynamicOptions] = useState({});

  const fetchWithCache = async (endpoint) => {
    if (apiCacheRef.current[endpoint]) {
      return apiCacheRef.current[endpoint];
    }

    const data = await fetchDynamicAPIS(endpoint);
    apiCacheRef.current[endpoint] = data;
    return data;
  };

  useEffect(() => {
    apiCacheRef.current = {};
    fetchingRef.current = {};
  }, [id]);

  // 1. Create a helper to get current dependency values for ALL fields
  const getAllDependencyValues = (formData) => {
    const allFields = formData.sections.flatMap((s) => s.fields || []);
    const deps = {};
    allFields.forEach((field) => {
      // Only track fields that are dependencies for others
      deps[field.input_uuid] = field.value;
    });
    return deps;
  };

  // 2. Inside DynamicPreview component:
  const currentDependencyValues = useMemo(
    () => getAllDependencyValues(formData),
    [formData],
  );

  useEffect(() => {
    if (!formData?.sections?.length) return;

    const processDropdowns = async () => {
      const allFields = formData.sections.flatMap((s) => s.fields || []);
      // const newOptionsMap = { ...dynamicOptions };
      const newOptionsMap = structuredClone(dynamicOptions);
      const newFetchedFields = { ...fetchedFields };
      let hasChanges = false;

      const promises = allFields.map(async (field) => {
        const type = field?.property?.field_type;

        if (
          !["Input Dropdown", "Input Radio", "Input Checkbox"].includes(type)
        ) {
          return null;
        }

        const config =
          field?.property?.input_dropdown ||
          field?.property?.input_radio ||
          field?.property?.input_checkbox;

        if (!config) return null;

        const fieldUuid = field.input_uuid;

        // MASTER FIELD
        if (config.is_master && config.source_endpoint) {
          if (!fetchedFields[fieldUuid]) {
            let endpoint = config.source_endpoint;

            const params = new URLSearchParams();

            Object.entries(config.default_query_params || {}).forEach(
              ([key, value]) => {
                params.append(key, value);
              },
            );

            if ([...params.keys()].length) {
              endpoint += `?${params.toString()}`;
            }

            if (fetchingRef.current[endpoint]) return null;

            try {
              fetchingRef.current[endpoint] = true;

              setFieldLoading((prev) => ({
                ...prev,
                [fieldUuid]: true,
              }));

              const data = await fetchWithCache(endpoint);

              const items = data?.items || [];

              return {
                fieldUuid,
                items,
                type: "master",
              };
            } finally {
              fetchingRef.current[endpoint] = false;

              setFieldLoading((prev) => ({
                ...prev,
                [fieldUuid]: false,
              }));
            }
          }
        }

        if (config.is_dependent) {
          const dependencies = config.master_dependencies || [];

          const dependencyValues = getFieldDependencies(field, allFields);

          const allDependenciesFilled = dependencyValues.every(
            (dep) => dep.value,
          );

          const preloadData = config?.is_data_visible === true;

          // if (!allDependenciesFilled && !preloadData) {
          //   return {
          //     fieldUuid,
          //     items: [],
          //     type: "dependent-clear",
          //   };
          // }

          // FIX: If the field is hidden (is_visible is true and no value),
          // do not proceed with the API call logic.
          if (config.is_visible === true && !allDependenciesFilled) {
            return {
              fieldUuid,
              items: [],
              type: "dependent-clear",
            };
          }

          // For is_data_visible fields: fire fetch independently so its options
          // appear immediately — not blocked by Promise.all of other fields

          // Inside the processDropdowns function in DynamicPreview
          if (preloadData) {
            // 1. Calculate the current dependency state
            const dependencyValues = getFieldDependencies(field, allFields);
            const fetchKey = JSON.stringify(dependencyValues);
            const preloadFetchKey = `preload:${fieldUuid}:${fetchKey}`; // Include dependency values in the key

            // 2. Only skip if the EXACT dependency state has already been fetched
            if (fetchedFields[fieldUuid] === preloadFetchKey) {
              return null;
            }

            let endpoint = config.source_endpoint;
            if (!endpoint) return null;

            const params = new URLSearchParams();
            Object.entries(config.default_query_params || {}).forEach(
              ([key, value]) => {
                params.append(key, value);
              },
            );

            // 3. IMPORTANT: Append dependency values to the endpoint even for preloaded fields
            dependencyValues.forEach((dep) => {
              if (dep.value) {
                params.append(dep.query_param, dep.value);
              }
            });

            endpoint += `?${params.toString()}`;

            if (fetchingRef.current[endpoint]) return null;

            fetchingRef.current[endpoint] = true;
            setFieldLoading((prev) => ({ ...prev, [fieldUuid]: true }));

            fetchWithCache(endpoint)
              .then((data) => {
                const items = data?.items || [];
                const options = items.map((item) => ({
                  input_field_uuid: fieldUuid,
                  label:
                    item.name ||
                    item.label ||
                    item?.title ||
                    item.first_name ||
                    "Unnamed",
                  value: item.id ?? item.value,
                  option_uuid: item.option_uuid || item.id || item.value,
                }));

                // 4. Update fetchedFields with the new dependency-aware key
                setFetchedFields((prev) => ({
                  ...prev,
                  [fieldUuid]: preloadFetchKey,
                }));
                setDynamicOptions((prev) => ({
                  ...prev,
                  [fieldUuid]: { field_uuid: fieldUuid, options },
                }));

                // Inside processDropdowns, before dispatching:
                if (hasChanges) {
                  // Check if options actually changed compared to current dynamicOptions
                  const isDifferent =
                    JSON.stringify(newOptionsMap) !==
                    JSON.stringify(dynamicOptions);

                  if (isDifferent) {
                    setFetchedFields((prev) => ({
                      ...prev,
                      ...newFetchedFields,
                    }));
                    setDynamicOptions((prev) => ({
                      ...prev,
                      ...newOptionsMap,
                    }));
                    dispatch({
                      type: "bmrFbProperty/setOptions",
                      payload: newOptionsMap,
                    });
                  }
                }

                dispatch({
                  type: "bmrFbProperty/setOptions",
                  payload: { [fieldUuid]: { field_uuid: fieldUuid, options } },
                });
              })
              .finally(() => {
                fetchingRef.current[endpoint] = false;
                setFieldLoading((prev) => ({ ...prev, [fieldUuid]: false }));
              });

            return null;
          }

          const fetchKey = JSON.stringify(dependencyValues);

          if (fetchedFields[fieldUuid] === fetchKey) {
            return null;
          }

          let endpoint = config.source_endpoint;

          if (!endpoint) return null;

          const params = new URLSearchParams();

          Object.entries(config.default_query_params || {}).forEach(
            ([key, value]) => {
              params.append(key, value);
            },
          );

          dependencyValues.forEach((dep) => {
            if (dep.value) {
              params.append(dep.query_param, dep.value);
            }
          });

          endpoint += `?${params.toString()}`;

          if (fetchingRef.current[endpoint]) return null;

          try {
            fetchingRef.current[endpoint] = true;

            setFieldLoading((prev) => ({
              ...prev,
              [fieldUuid]: true,
            }));

            const data = await fetchWithCache(endpoint);

            const items = data?.items || [];

            return {
              fieldUuid,
              items,
              fetchKey,
              type: "dependent",
            };
          } finally {
            fetchingRef.current[endpoint] = false;

            setFieldLoading((prev) => ({
              ...prev,
              [fieldUuid]: false,
            }));
          }
        }
        return null;
      });
      const results = await Promise.all(promises);

      results.forEach((result) => {
        if (!result) return;

        const { fieldUuid, items, fetchKey, type } = result;

        newOptionsMap[fieldUuid] = {
          field_uuid: fieldUuid,
          options: items.map((item) => ({
            input_field_uuid: fieldUuid,
            label:
              item.name ||
              item.label ||
              item?.title ||
              item.first_name ||
              "Unnamed",
            value: item.id ?? item.value,
            option_uuid: item.option_uuid || item.id || item.value,
          })),
        };

        if (type === "master") {
          newFetchedFields[fieldUuid] = "master_fetched";
        }

        if (type === "dependent") {
          newFetchedFields[fieldUuid] = fetchKey;
        }

        if (type === "dependent-clear") {
          delete newFetchedFields[fieldUuid];
        }

        hasChanges = true;
      });

      if (hasChanges) {
        setFetchedFields((prev) => ({ ...prev, ...newFetchedFields }));
        setDynamicOptions((prev) => ({ ...prev, ...newOptionsMap }));
        dispatch({
          type: "bmrFbProperty/setOptions",
          payload: newOptionsMap,
        });
      }
    };

    processDropdowns();
  }, [currentDependencyValues]);

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          py: 1,
          px: 4,
          borderBottom: "1px solid #E4F9F2",
          backgroundColor: "#E4F9F2",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Template Name: {templateName?.name}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {!cardId && (
            <Button
              variant="contained"
              onClick={handlePublish}
              sx={{
                borderRadius: "0.375rem !important",
                width: "7.18rem !important",
              }}
              disabled={templateName?.status === 2}
            >
              Publish
            </Button>
          )}
          <IconButton onClick={handleCloseDrawer} sx={{ color: "black" }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {loading ? (
        <Box className="p-3">
          <Skeleton variant="text" width="30%" height={20} />
          <Divider />
          <Skeleton variant="text" width="100%" height={60} />
          <Divider />
          <Skeleton variant="text" width="100%" height={60} />
        </Box>
      ) : (
        <Box className="p-4">
          <DynamicFormGenerator
            formJson={formData.sections}
            formData={formData}
            setDFormData={setDynamicFormData}
            dynamicOptions={dynamicOptions}
            fieldLoading={fieldLoading}
            loading={false}
          />
        </Box>
      )}

      <DeleteModal
        open={openConfirmation}
        setClose={handleCloseConfirmation}
        title="Template is not submitted yet, would you like to submit"
        buttonText="Submit"
        handleSuccess={handleSubmit}
        handleRejection={handleCloseConfirmation}
        rejectButtonText="Cancel"
      />
    </div>
  );
};

export default DynamicPreview;
